import { createContext, useContext, useReducer } from 'react';
import { useLoaderData } from 'react-router';

import { STORAGE_KEYS, Storage } from '../../../utils';
import { TdashboardLoader } from '../Dashboard';
import {
  FacilityProviderAction,
  FacilityProviderContextProps,
  FacilityProviderStates,
  FacilitySnapShot,
} from './FacilityProvider.types';

const initialStates: FacilityProviderStates = {
  selectedFacility: Storage.getItem(STORAGE_KEYS.ACTIVE_FACILITY) || '',
  facilityList: [],
  visitedFacilitySnapshots: {},
  facilitySnapshots: {},
};
const defaultValue = {
  state: initialStates,
  dispatch: () => {},
};

const FacilityContext =
  createContext<FacilityProviderContextProps>(defaultValue);

export const useFacilityContext = () => useContext(FacilityContext);
export const FacilityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(facilityReducer, initialStates);

  const { facilityList = [], primaryFacility } =
    useLoaderData() as TdashboardLoader;

  if (JSON.stringify(state.facilityList) !== JSON.stringify(facilityList)) {
    dispatch({
      type: FACILITY_ACTION_TYPES.SET_FACILITY_LIST,
      payload: facilityList,
    });
  }
  if (
    state.selectedFacility &&
    (!Array.isArray(facilityList) || facilityList.length === 0)
  ) {
    dispatch({
      type: FACILITY_ACTION_TYPES.SET_SELECTED_FACILITY,
      payload: '',
    });
  }

  if (
    !state.selectedFacility &&
    Array.isArray(facilityList) &&
    facilityList.length >= 0 &&
    primaryFacility
  ) {
    dispatch({
      type: FACILITY_ACTION_TYPES.SET_SELECTED_FACILITY,
      payload: primaryFacility || '',
    });
  }
  return (
    <FacilityContext.Provider value={{ state, dispatch }}>
      {children}
    </FacilityContext.Provider>
  );
};
export const facilityActions = {
  updateFacilityWiseGraphStatus: (
    facilityId: string,
    graphStatus: FacilitySnapShot['userGraphGenerationStatus'],
    clientId?: string
  ) => {
    return {
      type: FACILITY_ACTION_TYPES.UPDATE_FACILITY_WISE_GRAPH_STATUS,
      payload: {
        id: facilityId,
        status: graphStatus,
        clientId,
      },
    };
  },
  updateShiftSessionId: (
    facilityId: string,
    status: FacilitySnapShot['userShiftManageStatus'],
    shiftSessionId?: string
  ) => {
    return {
      type: FACILITY_ACTION_TYPES.UPDATE_SHIFT_SESSION_ID,
      payload: { id: facilityId, shiftSessionId, status },
    };
  },
  setSelectedParamVersionId: (
    facilityId: string,
    versionId: FacilitySnapShot['activeParamVersionId']
  ) => {
    return {
      type: FACILITY_ACTION_TYPES.SET_SELECTED_PARAM_VERSION_ID,
      payload: { id: facilityId, versionId },
    };
  },
};
function facilityReducer(
  state: FacilityProviderStates,
  action: FacilityProviderAction
): FacilityProviderStates {
  const { payload, type, currentFacilitySnapshot = {} } = action;
  switch (type) {
    case FACILITY_ACTION_TYPES.SET_SELECTED_FACILITY: {
      Storage.setItem(STORAGE_KEYS.ACTIVE_FACILITY, payload);
      return {
        ...state,
        selectedFacility: payload || '',
        visitedFacilitySnapshots: {
          ...state.visitedFacilitySnapshots,
          ...currentFacilitySnapshot,
        },
      };
    }
    case FACILITY_ACTION_TYPES.SET_FACILITY_LIST: {
      return {
        ...state,
        facilityList: payload || [],
      };
    }
    case FACILITY_ACTION_TYPES.UPDATE_FACILITY_SNAPSHOT: {
      return {
        ...state,
        facilitySnapshots: {
          ...state.facilitySnapshots,
          ...payload,
        },
      };
    }
    case FACILITY_ACTION_TYPES.UPDATE_SHIFT_SESSION_ID: {
      return {
        ...state,
        facilitySnapshots: {
          ...state.facilitySnapshots,
          [payload?.id]: {
            ...(state?.facilitySnapshots?.[payload?.id] || {}),
            shiftSessionId: [SHIFT_MANAGE_STATUS.SUCCESS].includes(
              payload?.status
            )
              ? payload?.shiftSessionId
              : payload?.status === SHIFT_MANAGE_STATUS.DEFAULT
                ? undefined
                : state?.facilitySnapshots?.[payload?.id]?.shiftSessionId,
            userShiftManageStatus: payload?.status || SHIFT_MANAGE_STATUS.IDLE,
          },
        },
      };
    }
    case FACILITY_ACTION_TYPES.SET_SELECTED_PARAM_VERSION_ID: {
      return {
        ...state,
        facilitySnapshots: {
          ...state.facilitySnapshots,
          [payload?.id]: {
            ...(state?.facilitySnapshots?.[payload?.id] || {}),
            activeParamVersionId: payload?.versionId,
          },
        },
      };
    }

    case FACILITY_ACTION_TYPES.UPDATE_FACILITY_WISE_GRAPH_STATUS: {
      return {
        ...state,
        facilitySnapshots: {
          ...state.facilitySnapshots,
          [payload?.id]: {
            ...(state?.facilitySnapshots?.[payload?.id] || {}),
            userGraphGenerationStatus:
              payload?.status || GRAPH_GENERATE_STATUS.IDLE_GENERATED_GRAPH,
            shiftSessionId:
              payload?.status === GRAPH_GENERATE_STATUS.DEFAULT_GENERATE_GRAPH
                ? undefined
                : state?.facilitySnapshots?.[payload?.id]?.shiftSessionId,
            clientId: [GRAPH_GENERATE_STATUS.APPLY_GENERATED_GRAPH].includes(
              payload?.status
            )
              ? payload?.clientId
              : payload?.status === GRAPH_GENERATE_STATUS.DEFAULT_GENERATE_GRAPH
                ? undefined
                : state?.facilitySnapshots?.[payload?.id]?.clientId,
          },
        },
      };
    }

    case FACILITY_ACTION_TYPES.UPDATE_VISITED_FACILITY_SNAPSHOT: {
      return {
        ...state,
        visitedFacilitySnapshots: {
          ...state.visitedFacilitySnapshots,
          ...payload,
        },
      };
    }

    default: {
      throw Error('Unknown action: ' + action.type);
    }
  }
}

export const FACILITY_ACTION_TYPES = {
  SET_SELECTED_FACILITY: 'SET_SELECTED_FACILITY',
  SET_FACILITY_LIST: 'SET_FACILITY_LIST',
  UPDATE_VISITED_FACILITY_SNAPSHOT: 'UPDATE_VISITED_FACILITY_SNAPSHOT',
  UPDATE_FACILITY_SNAPSHOT: 'UPDATE_FACILITY_SNAPSHOT',
  UPDATE_FACILITY_WISE_GRAPH_STATUS: 'UPDATE_FACILITY_WISE_GRAPH_STATUS',
  UPDATE_SHIFT_SESSION_ID: 'UPDATE_SHIFT_SESSION_ID',
  SET_SELECTED_PARAM_VERSION_ID: 'SET_SELECTED_PARAM_VERSION_ID',
} as const;

export const GRAPH_GENERATE_STATUS = {
  DEFAULT_GENERATE_GRAPH: 'DEFAULT_GENERATE_GRAPH',
  IN_PROGRESS_GENERATE_GRAPH: 'IN_PROGRESS_GENERATE_GRAPH',
  SUCCESS_GENERATE_GRAPH: 'SUCCESS_GENERATE_GRAPH',
  ERROR_GENERATE_GRAPH: 'ERROR_GENERATE_GRAPH',
  APPLY_GENERATED_GRAPH: 'APPLY_GENERATED_GRAPH',
  IDLE_GENERATED_GRAPH: 'IDLE_GENERATED_GRAPH',
} as const;

export const SHIFT_MANAGE_STATUS = {
  DEFAULT: 'DEFAULT',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  IDLE: 'IDLE',
} as const;
